<script setup lang="ts">
import { VisAxis } from "@unovis/vue";
import { eventBus } from "#bb/lib/eventBus";
import { computed } from "vue";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "X Axis editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this axis",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "X Axis",
    icon: "lucide:box-select",
  },
});

const xyTicks = inject("xy-ticks", {
  tickValues: computed(() => []),
  tickFormat: () => "",
});
const orientation = inject("xy-orientation", computed(() => "vertical"));

const tickCount = computed(() => definitionData.value?.tickCount ?? 5);
const label = computed(() => definitionData.value?.label ?? "");
const tickFormatAccessor = computed(() => definitionData.value?.tickFormat ?? "");

function sampleTicks(values: number[], count: number) {
  if (!Array.isArray(values) || values.length === 0) return values;
  if (!count || count <= 0 || values.length <= count) return values;
  const step = Math.ceil(values.length / count);
  return values.filter((_, i) => i % step === 0);
}

const tickValues = computed(() => {
  if (orientation.value !== "vertical") return undefined;
  return sampleTicks(xyTicks.tickValues.value as number[], tickCount.value);
});

const axisTickFormat = computed(() => {
  // For vertical orientation, use the category formatter or custom code
  if (orientation.value === "vertical") {
    if (!tickFormatAccessor.value) return xyTicks.tickFormat;
    try {
      return eval(tickFormatAccessor.value);
    } catch {
      return xyTicks.tickFormat;
    }
  }
  // For horizontal orientation, the X-axis is numeric; use the custom formatter or identity
  if (!tickFormatAccessor.value) return (v: number) => v;
  try {
    return eval(tickFormatAccessor.value);
  } catch {
    return (v: number) => v;
  }
});
</script>

<template>
  <div>
    <VisAxis
      type="x"
      :tickValues="tickValues"
      :tickFormat="axisTickFormat"
      :tickCount="tickCount"
      :label="label"
    />
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds"
export default {
  editor: () => import("#bb/components/editor/unovis/xy/x-axis.vue"),
  componentId: ComponentDefinitionIds.UNOVIS_XY_X_AXIS,
  id: "xyxaxis",
  icon: "lucide:box-select",
  type: "X Axis",
  description: "The Axis component has been designed to work together with XY Container.",
  default: { columnsSettings: { default: 1 } },
  categories: ["UNOVIS"],
  order: 12004,
}
</script>
